/* Approved decorative V6 carousel only. No cart, pricing, inventory or API calls. */
(() => {
  if (customElements.get("acqua-spring-v6")) return;

  class AcquaSpringV6 extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.photos = JSON.parse(this.querySelector("[data-asv6-photos]").textContent);
      this.stack = this.querySelector("[data-asv6-stack]");
      this.carousel = this.querySelector(".asv6-photo-carousel");
      this.front = this.querySelector(".asv6-print-front");
      this.middle = this.querySelector(".asv6-print-middle");
      this.back = this.querySelector(".asv6-print-back");
      this.rotation = this.querySelector("[data-asv6-rotation]");
      this.dialog = this.querySelector("[data-asv6-dialog]");
      this.reduced = matchMedia("(prefers-reduced-motion: reduce)");
      this.index = 0;
      this.busy = false;
      this.stack.classList.remove("asv6-is-turning", "asv6-no-transition");
      this.playing = !this.reduced.matches;
      this.onScreen = true;

      this.rotation.addEventListener("click", () => {
        this.playing = !this.playing;
        this.updateRotation(2000);
      }, { signal });
      this.querySelector("[data-asv6-next]").addEventListener("click", () => {
        this.pause();
        void this.advance(1, true);
      }, { signal });
      this.querySelector("[data-asv6-previous]").addEventListener("click", () => {
        this.pause();
        void this.advance(-1, true);
      }, { signal });
      this.carousel.addEventListener("focusin", event => {
        if (event.target !== this.rotation) this.pause();
      }, { signal });
      this.querySelectorAll("[data-asv6-open-offer]").forEach(button => {
        button.addEventListener("click", () => this.openDetails(false), { signal });
      });
      this.querySelectorAll("[data-asv6-open-delivery]").forEach(button => {
        button.addEventListener("click", () => this.openDetails(true), { signal });
      });
      this.querySelector("[data-asv6-close]").addEventListener("click", () => this.dialog.close(), { signal });
      // Native dialog supplies Escape handling, focus containment and focus return.
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.clearRotation();
        else this.scheduleRotation(2000);
      }, { signal });
      this.reduced.addEventListener("change", () => {
        if (this.reduced.matches) this.pause();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.onScreen = entries[0].isIntersecting;
        if (this.onScreen) this.scheduleRotation(2000);
        else this.clearRotation();
      }, { threshold: 0.3 });
      this.observer.observe(this.carousel);
      this.render();
      this.updateRotation(2000);
      this.setAttribute("data-asv6-ready", "");
      updateFooterLinks();
    }

    disconnectedCallback() {
      this.clearRotation();
      clearTimeout(this.animationTimer);
      this.finishAnimation?.();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.removeAttribute("data-asv6-ready");
      updateFooterLinks();
    }

    clearRotation() {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }

    setPhoto(frame, photo, active) {
      const image = frame.querySelector("img");
      image.src = photo.src;
      image.alt = active ? photo.alt : "";
    }

    render() {
      this.setPhoto(this.front, this.photos[this.index], true);
      this.setPhoto(this.middle, this.photos[(this.index + 1) % this.photos.length], false);
      this.setPhoto(this.back, this.photos[(this.index + 2) % this.photos.length], false);
      this.querySelector("[data-asv6-caption]").textContent = this.photos[this.index].caption;
      this.front.querySelector(".asv6-photo-edition").textContent =
        `${String(this.index + 1).padStart(2, "0")} / ${String(this.photos.length).padStart(2, "0")}`;
      this.front.setAttribute("aria-label", `Photograph ${this.index + 1} of ${this.photos.length}`);
    }

    async advance(delta = 1, manual = false) {
      if (this.busy || !this.isConnected) return;
      this.busy = true;
      const signal = this.controller.signal;
      const nextIndex = (this.index + delta + this.photos.length) % this.photos.length;
      const preload = new Image();
      preload.src = this.photos[nextIndex].src;
      try {
        await preload.decode();
      } catch {
        if (!signal.aborted) this.busy = false;
        return; // Preserve the current photograph if the next image cannot load.
      }
      if (signal.aborted) return;
      if (!this.reduced.matches && delta === 1) {
        this.stack.classList.add("asv6-is-turning");
        await new Promise(resolve => {
          this.finishAnimation = resolve;
          this.animationTimer = setTimeout(resolve, 620);
        });
        this.finishAnimation = null;
      }
      if (signal.aborted) return;
      this.index = nextIndex;
      this.stack.classList.add("asv6-no-transition");
      this.render();
      this.stack.classList.remove("asv6-is-turning");
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (signal.aborted) return;
      this.stack.classList.remove("asv6-no-transition");
      this.busy = false;
      if (manual) {
        this.querySelector("[data-asv6-announcement]").textContent =
          `Photograph ${this.index + 1} of ${this.photos.length}. ${this.photos[this.index].caption}.`;
      }
    }

    scheduleRotation(delay = 6000) {
      this.clearRotation();
      if (!this.playing || document.hidden || !this.onScreen || !this.isConnected) return;
      this.rotationTimer = setTimeout(async () => {
        if (this.playing && !document.hidden && this.onScreen && !this.dialog.open) await this.advance();
        this.scheduleRotation();
      }, delay);
    }

    updateRotation(delay = 6000) {
      this.rotation.setAttribute("aria-label", this.playing ? "Pause photo rotation" : "Play photo rotation");
      this.rotation.setAttribute("aria-pressed", String(this.playing));
      const icon = document.createElement("span");
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = this.playing ? "Ⅱ" : "▶";
      this.rotation.replaceChildren(icon);
      this.scheduleRotation(delay);
    }

    pause() {
      this.playing = false;
      this.updateRotation();
    }

    openDetails(deliveryOnly) {
      this.pause();
      this.querySelector("[data-asv6-product-terms]").hidden = deliveryOnly;
      this.querySelector("[data-asv6-heading]").textContent = deliveryOnly ? "Delivery details" : "Offer details";
      if (!this.dialog.open) this.dialog.showModal();
    }
  }

  function updateFooterLinks() {
    const present = Boolean(document.querySelector("acqua-spring-v6[data-asv6-ready]"));
    document.querySelectorAll("[data-asv6-footer-details]").forEach(button => {
      button.hidden = !present;
    });
  }

  customElements.define("acqua-spring-v6", AcquaSpringV6);
  document.addEventListener("click", event => {
    const button = event.target instanceof Element ? event.target.closest("[data-asv6-footer-details]") : null;
    if (button && !button.hidden) {
      document.querySelector("acqua-spring-v6[data-asv6-ready]")?.openDetails(false);
    }
  });
  document.addEventListener("shopify:section:load", updateFooterLinks);
  document.addEventListener("DOMContentLoaded", updateFooterLinks, { once: true });
})();
